import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  Users, 
  Plus, 
  Search, 
  Mail, 
  Phone, 
  Calendar, 
  Filter, 
  TrendingUp,
  Clock,
  Trash2,
  Edit2
} from 'lucide-react';
import { API_BASE_URL } from '../../config';

const STAGES = [
  { id: 'NEW', label: 'New Lead', color: 'blue' },
  { id: 'NOT_ANSWERED', label: 'Not Answered', color: 'slate' },
  { id: 'FOLLOW_UP', label: 'Follow Up Scheduled', color: 'amber' },
  { id: 'DEMO', label: 'Demo Scheduled', color: 'indigo' },
  { id: 'INTERESTED', label: 'Interested', color: 'purple' },
  { id: 'INITIAL_REGISTRATION', label: 'Initial Registration', color: 'cyan' },
  { id: 'REGISTERED', label: 'Converted', color: 'emerald' },
  { id: 'LOST', label: 'Lost', color: 'rose' }
];

export default function Enquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [formData, setFormData] = useState({
    studentName: '',
    phone: '',
    email: '',
    courseInterested: '',
    source: '',
    status: 'NEW',
    notes: '',
    nextFollowUpDate: ''
  });

  // Horizontal Scroll Controls
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e) => {
    // Don't drag if clicking buttons or inputs
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select') || e.target.closest('textarea')) return;
    
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Scroll speed multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const fetchEnquiries = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/enquiries`);
      setEnquiries(res.data);
      setLoading(false);
    } catch (err) {
      toast.error('Failed to fetch enquiries');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedLead) {
        await axios.put(`${API_BASE_URL}/api/enquiries/${selectedLead.id}`, formData);
        toast.success('Enquiry updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/api/enquiries`, formData);
        toast.success('New enquiry added');
      }
      setIsModalOpen(false);
      resetForm();
      fetchEnquiries();
    } catch (err) {
      toast.error('Error saving enquiry');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this enquiry?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/enquiries/${id}`);
        toast.success('Enquiry deleted');
        fetchEnquiries();
      } catch (err) {
        toast.error('Error deleting enquiry');
      }
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/enquiries/${id}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchEnquiries();
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const openModal = (lead = null) => {
    if (lead) {
      setSelectedLead(lead);
      setFormData({
        studentName: lead.studentName || '',
        phone: lead.phone || '',
        email: lead.email || '',
        courseInterested: lead.courseInterested || '',
        source: lead.source || '',
        status: lead.status || 'NEW',
        notes: lead.notes || '',
        nextFollowUpDate: lead.nextFollowUpDate ? lead.nextFollowUpDate.substring(0, 16) : ''
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setSelectedLead(null);
    setFormData({
      studentName: '',
      phone: '',
      email: '',
      courseInterested: '',
      source: '',
      status: 'NEW',
      notes: '',
      nextFollowUpDate: ''
    });
  };

  const filteredEnquiries = enquiries.filter(e => 
    e.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.phone?.includes(searchTerm) ||
    e.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStagnationInfo = (lead) => {
    if (lead.status === 'REGISTERED' || lead.status === 'LOST') return null;
    
    // Use statusUpdatedAt if available, otherwise fallback to updatedAt
    const dateToUse = lead.statusUpdatedAt || lead.updatedAt;
    if (!dateToUse) return null;

    const diffMs = new Date() - new Date(dateToUse);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffDays >= 10) return { type: 'critical', label: 'Stagnant (10+ days)' };
    if (diffDays >= 7) return { type: 'warning', label: 'Stagnant (7 days)' };
    return null;
  };

  const stats = {
    total: enquiries.length,
    converted: enquiries.filter(e => e.status === 'REGISTERED').length,
    active: enquiries.filter(e => e.status !== 'REGISTERED' && e.status !== 'LOST').length,
    rate: enquiries.length > 0 
      ? Math.round((enquiries.filter(e => e.status === 'REGISTERED').length / enquiries.length) * 100) 
      : 0
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100">
      {/* Header & Stats */}
      <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white">Enquiries Funnel</h1>
          <p className="mt-1 text-slate-400">Manage and track your prospective students through the enrollment funnel.</p>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/40 p-4 backdrop-blur-xl">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400">
              <Users size={20} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total Leads</p>
              <p className="text-xl font-bold text-white">{stats.total}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/40 p-4 backdrop-blur-xl">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400">
              <TrendingUp size={20} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Conversions</p>
              <p className="text-xl font-bold text-white">{stats.converted}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/40 p-4 backdrop-blur-xl">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-400">
              <Clock size={20} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Active Leads</p>
              <p className="text-xl font-bold text-white">{stats.active}</p>
            </div>
          </div>

          <button 
            onClick={() => openModal()}
            className="flex items-center gap-2 rounded-2xl bg-blue-600 px-6 py-4 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition-all hover:bg-blue-500 hover:shadow-blue-500/40"
          >
            <Plus size={18} /> Add Enquiry
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <input 
            type="text"
            placeholder="Search leads by name, phone or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-2xl border border-slate-800 bg-slate-900/60 py-3 pl-12 pr-4 text-sm text-slate-200 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all"
          />
        </div>
        <div className="flex items-center gap-2 text-sm font-medium text-slate-400">
          <Filter size={16} />
          <span>Conversion Rate: <span className="text-emerald-400">{stats.rate}%</span></span>
        </div>
      </div>

      {/* Funnel Board */}
      <div 
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        className={`flex gap-4 overflow-x-auto pb-6 snap-x custom-scrollbar ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
      >
        {STAGES.map(stage => (
          <div key={stage.id} className="flex min-w-[260px] flex-col gap-3">
            <div className="px-2">
              <div className="flex items-center gap-2 mb-1.5 flex-1 min-w-0">
                <div className={`h-2 w-2 rounded-full bg-${stage.color}-500 shrink-0`}></div>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 truncate">{stage.label}</h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-lg bg-slate-950 px-2.5 py-1 text-[10px] font-bold text-slate-400 border border-slate-800 shadow-sm shadow-black/40">
                  {enquiries.filter(e => e.status === stage.id).length} Leads
                </span>
              </div>
            </div>

            <div className="flex flex-1 flex-col gap-2.5 rounded-2xl bg-slate-950/50 p-2 border border-slate-900">
              {filteredEnquiries
                .filter(e => e.status === stage.id)
                .map(lead => {
                  const stagnation = getStagnationInfo(lead);
                  const isStagnant = stagnation !== null;

                  return (
                    <div 
                      key={lead.id}
                      className={`group relative rounded-xl border p-3 transition-all ${
                        stagnation?.type === 'critical' 
                          ? 'border-rose-500/50 bg-rose-500/5 hover:border-rose-500/80 hover:bg-rose-500/10 shadow-lg shadow-rose-500/10' 
                          : stagnation?.type === 'warning'
                          ? 'border-amber-500/50 bg-amber-500/5 hover:border-amber-500/80 hover:bg-amber-500/10 shadow-lg shadow-amber-500/10'
                          : 'border-slate-800 bg-slate-900/40 hover:border-slate-700 hover:bg-slate-900/60'
                      }`}
                    >
                      {isStagnant && (
                        <div className={`absolute -top-2 -right-2 flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[8px] font-bold uppercase tracking-wider border backdrop-blur-md animate-pulse ${
                          stagnation.type === 'critical' 
                            ? 'bg-rose-500/20 text-rose-400 border-rose-500/30' 
                            : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                        }`}>
                          <Clock size={8} />
                          {stagnation.label}
                        </div>
                      )}
                    <div className="flex items-start justify-between">
                      <h4 className="text-sm font-bold text-slate-200 group-hover:text-white transition-colors">{lead.studentName}</h4>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openModal(lead)} className="p-1 text-slate-500 hover:text-blue-400">
                          <Edit2 size={14} />
                        </button>
                        <button onClick={() => handleDelete(lead.id)} className="p-1 text-slate-500 hover:text-rose-400">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    
                    <p className="mt-0.5 text-[11px] text-blue-400 font-medium">{lead.courseInterested || 'General Inquiry'}</p>
                    
                    <div className="mt-3 flex flex-col gap-1.5">
                      <div className="flex items-center gap-2 text-[10px] text-slate-500">
                        <Phone size={12} />
                        <span>{lead.phone}</span>
                      </div>
                      {lead.email && (
                        <div className="flex items-center gap-2 text-[10px] text-slate-500">
                          <Mail size={12} />
                          <span className="truncate">{lead.email}</span>
                        </div>
                      )}
                      {lead.nextFollowUpDate && (
                        <div className="mt-2 flex items-center gap-2 rounded-lg bg-blue-500/5 px-2 py-1 text-[10px] text-blue-400 border border-blue-500/10">
                          <Calendar size={12} />
                          <span>Follow-up: {new Date(lead.nextFollowUpDate).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 pt-2 border-t border-slate-800/50">
                      <select 
                        value={lead.status}
                        onChange={(e) => updateStatus(lead.id, e.target.value)}
                        className="w-full bg-transparent text-[10px] font-bold uppercase tracking-wider text-slate-500 focus:outline-none cursor-pointer hover:text-slate-300"
                      >
                        {STAGES.map(s => (
                          <option key={s.id} value={s.id} className="bg-slate-900 text-slate-200">{s.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })}
              
              {filteredEnquiries.filter(e => e.status === stage.id).length === 0 && (
                <div className="flex h-24 items-center justify-center rounded-xl border border-dashed border-slate-800">
                  <p className="text-xs text-slate-600">No leads</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 backdrop-blur-sm sm:p-6">
          <div className="absolute inset-0 bg-slate-950/60" onClick={() => setIsModalOpen(false)}></div>
          <div className="relative w-full max-w-2xl rounded-3xl border border-slate-800 bg-slate-900 p-8 shadow-2xl overflow-y-auto max-h-[90vh] custom-scrollbar animate-in zoom-in-95 duration-200">
            <h2 className="text-2xl font-bold text-white mb-6">
              {selectedLead ? 'Edit Enquiry' : 'Add New Enquiry'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Student Name *</label>
                  <input 
                    required
                    type="text"
                    value={formData.studentName}
                    onChange={(e) => setFormData({...formData, studentName: e.target.value})}
                    placeholder="Enter full name"
                    className="w-full rounded-xl border border-slate-800 bg-slate-950/50 p-3 text-sm text-slate-200 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Phone Number *</label>
                  <input 
                    required
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="000-000-0000"
                    className="w-full rounded-xl border border-slate-800 bg-slate-950/50 p-3 text-sm text-slate-200 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Email Address</label>
                  <input 
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="student@example.com"
                    className="w-full rounded-xl border border-slate-800 bg-slate-950/50 p-3 text-sm text-slate-200 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Course Interested</label>
                  <input 
                    type="text"
                    value={formData.courseInterested}
                    onChange={(e) => setFormData({...formData, courseInterested: e.target.value})}
                    placeholder="e.g. JEE Advance, NEET"
                    className="w-full rounded-xl border border-slate-800 bg-slate-950/50 p-3 text-sm text-slate-200 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Lead Source</label>
                  <select 
                    value={formData.source}
                    onChange={(e) => setFormData({...formData, source: e.target.value})}
                    className="w-full rounded-xl border border-slate-800 bg-slate-950/50 p-3 text-sm text-slate-200 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Select Source</option>
                    <option value="Facebook">Facebook Ads</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Google">Google Search</option>
                    <option value="Phone Call">Phone Call</option>
                    <option value="Walk-in">Walk-in</option>
                    <option value="Referral">Referral</option>
                    <option value="Website">Website</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Funnel Stage</label>
                  <select 
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full rounded-xl border border-slate-800 bg-slate-950/50 p-3 text-sm text-slate-200 focus:border-blue-500 focus:outline-none"
                  >
                    {STAGES.map(s => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Next Follow-up Date</label>
                  <input 
                    type="datetime-local"
                    value={formData.nextFollowUpDate}
                    onChange={(e) => setFormData({...formData, nextFollowUpDate: e.target.value})}
                    className="w-full rounded-xl border border-slate-800 bg-slate-950/50 p-3 text-sm text-slate-200 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Consolidated Notes</label>
                  <textarea 
                    rows={4}
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Enter lead requirements, past conversations, etc."
                    className="w-full rounded-xl border border-slate-800 bg-slate-950/50 p-3 text-sm text-slate-200 focus:border-blue-500 focus:outline-none resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 rounded-2xl border border-slate-800 py-4 text-sm font-bold text-slate-400 hover:bg-slate-800 hover:text-white transition-all"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="flex-1 rounded-2xl bg-blue-600 py-4 text-sm font-bold text-white shadow-lg shadow-blue-500/20 hover:bg-blue-500 transition-all"
                >
                  {selectedLead ? 'Save Changes' : 'Create Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}



      {/* Styles for scrollbar */}
      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar {
          height: 6px;
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #334155;
          border-radius: 10px;
          border: 2px solid #020617;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #475569;
        }
      `}} />
    </div>
  );
}
